/**
 * Org Service — definición ÚNICA de la API HTTPS (`/org/v1/*`, T4/T5): rutas, parámetros, formas de
 * respuesta y ejemplos. De acá salen las dos caras de la documentación (28/10/2026, pedido de Sol):
 *  - `GET /org/v1/openapi.json` (lo sirve la API, sin token) con `buildOrgOpenApi`;
 *  - la página /org/api de Internal, que la renderiza con la base URL y el endpoint de token reales.
 * Si cambia una ruta en functions/org-api/handler.ts, cambia acá en el mismo PR (el test lo cruza).
 * Pura: sin I/O.
 */
import { ORG_ACCESS_STATUSES } from './types/org';

export const ORG_API_VERSION = 'v1';
export const ORG_API_PREFIX = `/org/${ORG_API_VERSION}`;
export const ORG_READ_SCOPE_NAME = 'org/read';

export type OrgApiAudience = 'anyone' | 'person' | 'self_roster_service' | 'self_leader_roster_service' | 'scoped';

export interface OrgApiParam {
  name: string;
  in: 'path' | 'query';
  description: string;
  example: string;
}

export interface OrgApiRoute {
  method: 'GET';
  /** Con `{param}`: igual que en el handler. */
  path: string;
  summary: string;
  description: string;
  /** Quién puede llamarla (se traduce a texto en la página). */
  audience: OrgApiAudience;
  params?: OrgApiParam[];
  /** Nombre del schema de respuesta (clave de ORG_API_SCHEMAS). */
  response: keyof typeof ORG_API_SCHEMAS;
  /** Ejemplo de respuesta (JSON serializable, corto). */
  example: unknown;
  /** Status posibles además de 200 y 401. */
  errors?: Array<{ status: 400 | 403 | 404; when: string }>;
}

/** Schemas JSON (OpenAPI 3.1) de las formas de respuesta. Los nombres de campo son los de las tablas org.* salvo lo calculado (camelCase). */
export const ORG_API_SCHEMAS = {
  Error: {
    type: 'object',
    required: ['error'],
    properties: { error: { type: 'string', description: 'Motivo corto, sin datos internos' }, details: { description: 'Solo en 400: issues de validación (path, code, message)' } },
  },
  Person: {
    type: 'object',
    required: ['employee_id', 'email', 'full_name', 'status'],
    properties: {
      employee_id: { type: 'integer', description: 'Id estable de la persona (employeeId del Community Map)' },
      email: { type: 'string', format: 'email' },
      full_name: { type: 'string' },
      role_title: { type: ['string', 'null'], description: 'Puesto según el Community Map' },
      level: { type: ['string', 'null'] },
      area: { type: ['string', 'null'] },
      team: { type: ['string', 'null'] },
      solution_slug: { type: ['string', 'null'], description: 'Solución principal (creative-studio, paid-media, owned-media, tech-data-studio, growth-strategy…)' },
      people_manager_email: { type: ['string', 'null'], description: 'Líder directo' },
      status: { type: 'string', description: 'Estado en el Community Map (`Activo`; las listas solo traen activos)' },
    },
  },
  Leader: { type: ['object', 'null'], properties: { email: { type: 'string' }, name: { type: 'string' } } },
  Solution: {
    type: ['object', 'null'],
    properties: { slug: { type: 'string' }, name: { type: 'string' }, worksWithClients: { type: 'boolean' }, source: { type: 'string' } },
  },
  Assignment: {
    type: 'object',
    required: ['id', 'employee_id', 'client_id', 'solution_slug', 'period', 'origin', 'status'],
    properties: {
      id: { type: 'string', description: '<employee_id>:<client_id>:<solution_slug>:<YYYY-MM>' },
      employee_id: { type: 'integer' },
      full_name: { type: 'string' },
      email: { type: 'string' },
      client_id: { type: 'string', description: 'Id del cliente en el Brain' },
      display_name: { type: 'string', description: 'Nombre del cliente' },
      solution_slug: { type: 'string' },
      period: { type: 'string', description: 'Mes YYYY-MM' },
      pct: { type: ['number', 'null'], description: 'Dedicación 0–100' },
      role_title: { type: ['string', 'null'] },
      origin: { type: 'string', enum: ['capacity', 'declared', 'inferred'] },
      status: { type: 'string', enum: ['confirmed', 'suggested', 'closed'] },
      manager_email: { type: ['string', 'null'] },
      confirmed_by: { type: ['string', 'null'] },
      confirmed_at: { type: ['string', 'null'], format: 'date-time' },
    },
  },
  Access: {
    type: 'object',
    required: ['canView', 'canViewAll', 'canAdmin', 'isLeader', 'scope'],
    properties: {
      canView: { type: 'boolean' },
      canViewAll: { type: 'boolean', description: 'Roster del servicio, admins de Internal o sistema' },
      canAdmin: { type: 'boolean' },
      isLeader: { type: 'boolean' },
      scope: { type: 'string', enum: ['all', 'subtree', 'self'] },
      subtreeEmails: { type: 'array', items: { type: 'string' } },
    },
  },
  Me: {
    type: 'object',
    properties: {
      person: { $ref: '#/components/schemas/Person', nullable: true },
      solution: { $ref: '#/components/schemas/Solution' },
      leader: { $ref: '#/components/schemas/Leader' },
      assignments: { type: 'array', items: { $ref: '#/components/schemas/Assignment' } },
      access: { $ref: '#/components/schemas/Access' },
      periods: { type: 'array', items: { type: 'string' }, description: 'Meses vigentes: anterior y en curso' },
    },
  },
  PersonView: {
    type: 'object',
    properties: {
      person: { $ref: '#/components/schemas/Person' },
      solution: { $ref: '#/components/schemas/Solution' },
      leader: { $ref: '#/components/schemas/Leader' },
      assignments: { type: ['array', 'null'], items: { $ref: '#/components/schemas/Assignment' }, description: 'null cuando quien pregunta no puede ver sus clientes' },
      assignmentsHidden: { type: 'boolean' },
    },
  },
  Scopes: {
    type: 'object',
    properties: {
      email: { type: 'string' },
      employeeId: { type: ['integer', 'null'] },
      solutionSlug: { type: ['string', 'null'] },
      solutionSlugs: { type: 'array', items: { type: 'string' } },
      clientIds: { type: 'array', items: { type: 'string' }, description: 'Clientes vigentes (confirmados y sugeridos)' },
      confirmedClientIds: { type: 'array', items: { type: 'string' }, description: 'Usar esto para autorizar acceso a datos de un cliente' },
    },
  },
  Team: {
    type: 'object',
    properties: {
      leaderEmail: { type: 'string' },
      people: { type: 'array', items: { allOf: [{ $ref: '#/components/schemas/Person' }, { type: 'object', properties: { depth: { type: 'integer' }, isDirect: { type: 'boolean' }, directLeaderName: { type: 'string' } } }] } },
      assignments: { type: 'array', items: { $ref: '#/components/schemas/Assignment' } },
      missingFromOrg: { type: 'array', items: { type: 'string' }, description: 'Mails del subárbol que todavía no están en org.person' },
    },
  },
  AccessItem: {
    type: 'object',
    properties: {
      toolSlug: { type: 'string' },
      toolName: { type: 'string' },
      clientId: { type: ['string', 'null'], description: 'null si la herramienta no es por cliente' },
      toolRole: { type: ['string', 'null'] },
      moment: { type: ['string', 'null'], enum: ['day1', 'on_assignment', null] },
      approverEmail: { type: ['string', 'null'] },
      policyId: { type: ['integer', 'null'] },
      state: { type: 'string', enum: ['missing', 'requested', 'granted', 'extra'] },
      accessId: { type: ['integer', 'null'] },
      evidenceSystem: { type: ['string', 'null'], enum: ['vault', 'cognito', 'google-group', 'external', null] },
      evidenceRef: { type: ['string', 'null'], description: 'Solo para el roster; para el resto viene null' },
    },
  },
  EffectiveAccess: {
    type: 'object',
    description: 'Un acceso registrado (org.effective_access), con o sin política que lo pida',
    properties: {
      id: { type: 'integer' }, employee_id: { type: 'integer' }, tool_slug: { type: 'string' }, client_id: { type: ['string', 'null'] },
      status: { type: 'string', enum: [...ORG_ACCESS_STATUSES] },
      granted_by: { type: ['string', 'null'] }, granted_at: { type: ['string', 'null'], format: 'date-time' }, revoked_at: { type: ['string', 'null'], format: 'date-time' },
      evidence_system: { type: ['string', 'null'] }, evidence_ref: { type: ['string', 'null'], description: 'Solo para el roster; para el resto viene null' },
      updated_at: { type: 'string', format: 'date-time' },
    },
  },
  AccessView: {
    type: 'object',
    properties: {
      person: { $ref: '#/components/schemas/Person' },
      solution: { $ref: '#/components/schemas/Solution' },
      assignments: { type: 'array', items: { $ref: '#/components/schemas/Assignment' } },
      access: { type: 'array', items: { $ref: '#/components/schemas/EffectiveAccess' } },
      items: { type: 'array', items: { $ref: '#/components/schemas/AccessItem' } },
      summary: { type: 'object', properties: { granted: { type: 'integer' }, requested: { type: 'integer' }, missing: { type: 'integer' }, extra: { type: 'integer' } } },
    },
  },
  SolutionCounts: {
    type: 'object',
    properties: {
      slug: { type: 'string' }, name: { type: 'string' }, worksWithClients: { type: 'boolean' },
      counts: { type: 'object', properties: { people: { type: 'integer' }, assigned_people: { type: 'integer' }, clients: { type: 'integer' }, assignments: { type: 'integer' }, confirmed: { type: 'integer' } } },
    },
  },
  SolutionView: {
    type: 'object',
    properties: {
      solution: { $ref: '#/components/schemas/Solution' },
      people: { type: 'array', items: { $ref: '#/components/schemas/Person' } },
      clients: { type: 'array', items: { $ref: '#/components/schemas/ClientCounts' } },
      assignments: { type: 'array', items: { $ref: '#/components/schemas/Assignment' }, description: 'Solo las aristas visibles para quien pregunta' },
      assignmentsScope: { type: 'string', enum: ['all', 'subtree', 'self'] },
      sheet: { $ref: '#/components/schemas/SolutionSheet' },
    },
  },
  SolutionSheet: {
    type: 'object',
    description: 'Ficha de la solución: qué hace, servicios, cómo trabaja, roles, cómo pedirle trabajo y links. La mantiene su dueño; nunca trae precios, costos ni datos de clientes.',
    properties: {
      sections: {
        type: 'array',
        items: {
          type: 'object',
          properties: {
            id: { type: 'string', description: 'what · services · how · roles · request · links (o una sección propia)' },
            title: { type: 'string' },
            intro: { type: 'string' },
            items: { type: 'array', items: { type: 'object', properties: { title: { type: 'string' }, description: { type: 'string' }, url: { type: 'string' }, tag: { type: 'string' } } } },
          },
        },
      },
      updatedAt: { type: ['string', 'null'], format: 'date-time' },
      updatedBy: { type: ['string', 'null'] },
      source: { type: 'string', enum: ['org', 'oml-home'], description: '`oml-home`: Owned Media mantiene su ficha en su Inicio' },
      editPath: { type: ['string', 'null'] },
      ownerEmail: { type: ['string', 'null'], description: 'Dueño de la solución: edita su ficha' },
      completeness: { type: 'object', properties: { filled: { type: 'integer' }, total: { type: 'integer' }, missing: { type: 'array', items: { type: 'string' } } } },
      canEdit: { type: 'boolean', description: 'Si quien pregunta puede editarla (en el BO)' },
    },
  },
  ClientCounts: {
    type: 'object',
    properties: {
      client_id: { type: 'string' }, display_name: { type: 'string' }, pod: { type: ['string', 'null'] },
      lead_email: { type: ['string', 'null'] }, growth_partner_email: { type: ['string', 'null'] },
      people: { type: 'integer' }, confirmed: { type: 'integer' },
    },
  },
  ClientView: {
    type: 'object',
    properties: {
      client: { type: 'object', properties: { client_id: { type: 'string' }, display_name: { type: 'string' }, pod: { type: ['string', 'null'] }, lead_email: { type: ['string', 'null'] }, growth_partner_email: { type: ['string', 'null'] } } },
      peopleCount: { type: 'integer' },
      confirmedCount: { type: 'integer' },
      solutions: { type: 'array', items: { type: 'string' } },
      assignments: { type: 'array', items: { $ref: '#/components/schemas/Assignment' }, description: 'Solo las aristas visibles para quien pregunta' },
      assignmentsScope: { type: 'string', enum: ['all', 'subtree', 'self'] },
    },
  },
  CanI: {
    type: 'object',
    required: ['email', 'clientId', 'allowed', 'status'],
    properties: {
      email: { type: 'string' },
      clientId: { type: 'string' },
      allowed: { type: 'boolean', description: 'true solo con una asignación CONFIRMADA vigente' },
      status: { type: 'string', enum: ['confirmed', 'suggested', 'none', 'person_not_found', 'client_not_found'] },
      solutionSlugs: { type: 'array', items: { type: 'string' } },
    },
  },
  Index: { type: 'object', properties: { service: { type: 'string' }, version: { type: 'string' }, routes: { type: 'array', items: { type: 'string' } } } },
} as const;

// Ejemplos FICTICIOS (el openapi se sirve sin token): ids fuera de rango y mails que no existen.
const EX_PERSON = { employee_id: 99999, email: 'persona.ejemplo@winclap.com', full_name: 'PERSONA EJEMPLO', role_title: 'Paid Media Analyst', level: 'Semi Senior', area: 'Paid Media Lab', team: 'Pod Ejemplo', solution_slug: 'paid-media', people_manager_email: 'lider.ejemplo@winclap.com', status: 'Activo' };
const EX_ASSIGNMENT = { id: '99999:cliente-demo:paid-media:2026-10', employee_id: 99999, full_name: 'PERSONA EJEMPLO', email: 'persona.ejemplo@winclap.com', client_id: 'cliente-demo', display_name: 'Cliente Demo', solution_slug: 'paid-media', period: '2026-10', pct: 50, role_title: 'Paid Media Analyst', origin: 'capacity', status: 'confirmed', manager_email: 'lider.ejemplo@winclap.com', confirmed_by: 'lider.ejemplo@winclap.com', confirmed_at: '2026-10-27T10:30:00.000Z' };

export const ORG_API_ROUTES: readonly OrgApiRoute[] = [
  { method: 'GET', path: '/org/v1', summary: 'Índice', description: 'Nombre del servicio, versión y lista de rutas.', audience: 'anyone', response: 'Index', example: { service: 'winclap-org', version: 'v1', routes: ['GET /org/v1', 'GET /org/v1/me'] } },
  { method: 'GET', path: '/org/v1/me', summary: 'Mi ficha', description: 'La persona que llama: su ficha, su líder, sus clientes de hoy y su acceso al servicio. Un token de sistema recibe 400 (no tiene ficha).', audience: 'person', response: 'Me', example: { person: EX_PERSON, solution: { slug: 'paid-media', name: 'Paid Media Lab', worksWithClients: true, source: 'allocator' }, leader: { email: 'lider.ejemplo@winclap.com', name: 'LIDER EJEMPLO' }, assignments: [EX_ASSIGNMENT], access: { canView: true, canViewAll: false, canAdmin: false, isLeader: false, scope: 'self', subtreeEmails: [] }, periods: ['2026-09', '2026-10'] }, errors: [{ status: 400, when: 'El token es de un sistema' }] },
  { method: 'GET', path: '/org/v1/people', summary: 'Directorio', description: 'Personas activas con puesto, área, team, solución y líder. Sin clientes.', audience: 'anyone', response: 'Person', example: [EX_PERSON] },
  { method: 'GET', path: '/org/v1/people/{email}', summary: 'Ficha de una persona', description: 'Su ficha y, si quien pregunta puede verlos, sus clientes de hoy; si no, `assignments` viene null y `assignmentsHidden` true.', audience: 'scoped', params: [{ name: 'email', in: 'path', description: 'Mail de Winclap (URL-encoded)', example: 'persona.ejemplo%40winclap.com' }], response: 'PersonView', example: { person: EX_PERSON, solution: { slug: 'paid-media', name: 'Paid Media Lab', worksWithClients: true, source: 'allocator' }, leader: { email: 'lider.ejemplo@winclap.com', name: 'LIDER EJEMPLO' }, assignments: [EX_ASSIGNMENT], assignmentsHidden: false }, errors: [{ status: 404, when: 'La persona no está en el Org Service' }] },
  { method: 'GET', path: '/org/v1/people/{email}/scopes', summary: 'Scopes de una persona', description: 'La primitiva de autorización: qué clientes y soluciones puede ver hoy. Para autorizar acceso a datos de un cliente usar `confirmedClientIds`.', audience: 'self_roster_service', params: [{ name: 'email', in: 'path', description: 'Mail de Winclap (URL-encoded)', example: 'persona.ejemplo%40winclap.com' }], response: 'Scopes', example: { email: 'persona.ejemplo@winclap.com', employeeId: 99999, solutionSlug: 'paid-media', solutionSlugs: ['paid-media'], clientIds: ['cliente-demo', 'otro-cliente'], confirmedClientIds: ['cliente-demo'] }, errors: [{ status: 403, when: 'Una persona pregunta por otra sin estar en el roster' }, { status: 404, when: 'La persona no está en el Org Service' }] },
  { method: 'GET', path: '/org/v1/people/{email}/team', summary: 'Equipo de un líder', description: 'Reportes directos e indirectos del Community Map, con las asignaciones vigentes de cada uno.', audience: 'self_roster_service', params: [{ name: 'email', in: 'path', description: 'Mail del líder (URL-encoded)', example: 'lider.ejemplo%40winclap.com' }], response: 'Team', example: { leaderEmail: 'lider.ejemplo@winclap.com', people: [{ ...EX_PERSON, depth: 1, isDirect: true, directLeaderName: 'LIDER EJEMPLO' }], assignments: [EX_ASSIGNMENT], missingFromOrg: [] }, errors: [{ status: 403, when: 'Una persona pregunta por el equipo de otra sin estar en el roster' }, { status: 404, when: 'La persona no está en el Org Service' }] },
  { method: 'GET', path: '/org/v1/people/{email}/access', summary: 'Accesos de una persona', description: 'Qué accesos a herramientas le piden las políticas (según solución, puesto y clientes confirmados) y cuáles tiene, le faltan o le sobran.', audience: 'self_leader_roster_service', params: [{ name: 'email', in: 'path', description: 'Mail de Winclap (URL-encoded)', example: 'persona.ejemplo%40winclap.com' }], response: 'AccessView', example: { person: EX_PERSON, solution: { slug: 'paid-media', name: 'Paid Media Lab', worksWithClients: true, source: 'allocator' }, assignments: [EX_ASSIGNMENT], access: [{ id: 88, employee_id: 99999, tool_slug: 'meta-business-manager', client_id: 'cliente-demo', status: 'granted', granted_by: 'vault-sync', granted_at: '2026-10-28T10:00:00.000Z', revoked_at: null, evidence_system: 'vault', evidence_ref: null, updated_at: '2026-10-28T10:00:00.000Z' }], items: [{ toolSlug: 'meta-business-manager', toolName: 'Meta Business Manager', clientId: 'cliente-demo', toolRole: 'Administrar campañas', moment: 'on_assignment', approverEmail: 'lider.ejemplo@winclap.com', policyId: 12, state: 'granted', accessId: 88, evidenceSystem: 'vault', evidenceRef: null }], summary: { granted: 1, requested: 0, missing: 0, extra: 0 } }, errors: [{ status: 403, when: 'Quien pregunta no puede ver los accesos de esa persona' }, { status: 404, when: 'La persona no está en el Org Service' }] },
  { method: 'GET', path: '/org/v1/solutions', summary: 'Soluciones', description: 'Catálogo de soluciones con conteos: personas, asignadas, clientes, confirmadas.', audience: 'anyone', response: 'SolutionCounts', example: [{ slug: 'paid-media', name: 'Paid Media Lab', worksWithClients: true, counts: { people: 55, assigned_people: 42, clients: 23, assignments: 61, confirmed: 48 } }] },
  { method: 'GET', path: '/org/v1/solutions/{slug}', summary: 'Una solución', description: 'Su ficha (qué hace, servicios, cómo trabaja, roles, cómo pedirle trabajo y links), las personas de la solución, clientes con conteos y las asignaciones visibles para quien pregunta.', audience: 'scoped', params: [{ name: 'slug', in: 'path', description: 'Slug de la solución', example: 'paid-media' }], response: 'SolutionView', example: { solution: { slug: 'paid-media', name: 'Paid Media Lab', worksWithClients: true, source: 'allocator' }, people: [EX_PERSON], clients: [{ client_id: 'cliente-demo', display_name: 'Cliente Demo', pod: 'Pod Ejemplo', lead_email: null, growth_partner_email: 'gp.ejemplo@winclap.com', people: 6, confirmed: 5 }], assignments: [EX_ASSIGNMENT], assignmentsScope: 'all', sheet: { sections: [{ id: 'what', title: 'Qué hace', intro: 'Planifica, ejecuta y optimiza campañas de performance en medios pagos.', items: [] }, { id: 'services', title: 'Servicios', intro: '', items: [{ title: 'Media Mix Planning', description: 'Qué canales y con qué peso.', url: '', tag: '' }] }], updatedAt: '2026-11-03T15:00:00.000Z', updatedBy: 'lider.ejemplo@winclap.com', source: 'org', editPath: null, ownerEmail: 'lider.ejemplo@winclap.com', completeness: { filled: 2, total: 6, missing: ['how', 'roles', 'request', 'links'] }, canEdit: false } }, errors: [{ status: 404, when: 'La solución no existe' }] },
  { method: 'GET', path: '/org/v1/clients', summary: 'Clientes', description: 'Clientes con gente asignada hoy: conteos, sin nombres de personas.', audience: 'anyone', response: 'ClientCounts', example: [{ client_id: 'cliente-demo', display_name: 'Cliente Demo', pod: 'Pod Ejemplo', lead_email: null, growth_partner_email: 'gp.ejemplo@winclap.com', people: 6, confirmed: 5 }] },
  { method: 'GET', path: '/org/v1/clients/{clientId}', summary: 'Un cliente', description: 'Cuántos trabajan con el cliente (todos lo ven) y quiénes (según scope).', audience: 'scoped', params: [{ name: 'clientId', in: 'path', description: 'Id del cliente en el Brain', example: 'cliente-demo' }], response: 'ClientView', example: { client: { client_id: 'cliente-demo', display_name: 'Cliente Demo', pod: 'Pod Ejemplo', lead_email: null, growth_partner_email: 'gp.ejemplo@winclap.com' }, peopleCount: 6, confirmedCount: 5, solutions: ['creative-studio', 'paid-media'], assignments: [EX_ASSIGNMENT], assignmentsScope: 'all' }, errors: [{ status: 404, when: 'El cliente no existe en el Org Service' }] },
  { method: 'GET', path: '/org/v1/can-i', summary: '¿Trabaja con ese cliente?', description: 'Responde siempre con un booleano: `allowed` es true solo con una asignación confirmada vigente. Persona o cliente inexistentes no son 404: `allowed` false con el motivo en `status`.', audience: 'self_leader_roster_service', params: [{ name: 'person', in: 'query', description: 'Mail de la persona', example: 'persona.ejemplo@winclap.com' }, { name: 'client', in: 'query', description: 'Id del cliente en el Brain', example: 'cliente-demo' }], response: 'CanI', example: { email: 'persona.ejemplo@winclap.com', clientId: 'cliente-demo', allowed: true, status: 'confirmed', solutionSlugs: ['paid-media'] }, errors: [{ status: 403, when: 'Quien pregunta no puede ver las aristas de esa persona' }, { status: 400, when: 'Falta `person` o `client`' }] },
];

/** Tools del MCP del Org Service (misma Function URL del MCP, ruta /mcp). */
export const ORG_MCP_TOOLS = [
  { name: 'who_is', input: 'person', description: 'Quién es una persona: puesto, área, team, solución, líder y clientes de hoy (si tenés permiso para verlos).' },
  { name: 'my_team', input: 'leader?', description: 'Mi equipo (reportes directos e indirectos) con los clientes de cada uno. Con `leader`, el equipo de otra persona (solo roster o sistemas).' },
  { name: 'who_works_on', input: 'client', description: 'Quiénes trabajan hoy con un cliente: cuántos, en qué soluciones y los nombres que tenés permiso para ver.' },
  { name: 'solution_overview', input: 'solution', description: 'Una solución: qué hace y cómo trabaja (su ficha), cuánta gente, qué clientes y las asignaciones visibles.' },
  { name: 'can_i', input: 'person, client', description: '¿Una persona trabaja hoy con un cliente? Sí/no con el estado.' },
  { name: 'access_for', input: 'person', description: 'Qué accesos le corresponden a una persona y cuáles tiene, le faltan o le sobran.' },
] as const;

export interface OrgOpenApiOptions {
  /** Base URL de la API (Function URL de producción), sin barra final. */
  serverUrl: string;
  /** Endpoint de token de Cognito (client_credentials). */
  tokenUrl: string;
}

/** OpenAPI 3.1 a partir del registro: un documento por request (es chico). */
export function buildOrgOpenApi(opts: OrgOpenApiOptions) {
  const paths: Record<string, unknown> = {};
  for (const r of ORG_API_ROUTES) {
    const responses: Record<string, unknown> = {
      '200': { description: 'OK', content: { 'application/json': { schema: Array.isArray(r.example) ? { type: 'array', items: { $ref: `#/components/schemas/${r.response}` } } : { $ref: `#/components/schemas/${r.response}` }, example: r.example } } },
      '401': { description: 'Sin token válido', content: { 'application/json': { schema: { $ref: '#/components/schemas/Error' } } } },
    };
    for (const e of r.errors ?? []) responses[String(e.status)] = { description: e.when, content: { 'application/json': { schema: { $ref: '#/components/schemas/Error' } } } };
    paths[r.path] = {
      get: {
        summary: r.summary,
        description: `${r.description}\n\nQuién puede: ${audienceText(r.audience)}.`,
        operationId: r.path.replace(/^\/org\/v1\/?/, '').replace(/[{}]/g, '').replace(/[^a-z0-9]+/gi, '_').replace(/^_+|_+$/g, '') || 'index',
        parameters: (r.params ?? []).map((p) => ({ name: p.name, in: p.in, required: true, description: p.description, schema: { type: 'string' }, example: p.example })),
        responses,
        security: [{ bearer: [] }, { clientCredentials: [ORG_READ_SCOPE_NAME] }],
      },
    };
  }
  return {
    openapi: '3.1.0',
    info: {
      title: 'Winclap Org Service API',
      version: ORG_API_VERSION,
      description: 'Quién es cada persona, en qué solución trabaja, con qué clientes y qué accesos le corresponden. Solo lectura. Las asignaciones las confirma el líder directo en Internal (/org); las políticas las carga el roster del servicio.',
    },
    servers: [{ url: opts.serverUrl }],
    paths,
    components: {
      schemas: ORG_API_SCHEMAS,
      securitySchemes: {
        bearer: { type: 'http', scheme: 'bearer', bearerFormat: 'JWT', description: 'ID token o access token de Cognito de una persona (login de Internal o conector OAuth).' },
        clientCredentials: { type: 'oauth2', flows: { clientCredentials: { tokenUrl: opts.tokenUrl, scopes: { [ORG_READ_SCOPE_NAME]: 'Leer el directorio y las asignaciones persona × cliente' } } }, description: 'Sistemas: app client de servicio del pool, uno por consumidor; el token dura 60 minutos.' },
      },
    },
  };
}

/** Texto de quién puede llamar una ruta (para el OpenAPI; la página usa i18n). */
export function audienceText(a: OrgApiAudience): string {
  switch (a) {
    case 'anyone': return 'cualquier persona autenticada o sistema';
    case 'person': return 'solo personas (no sistemas)';
    case 'self_roster_service': return 'la persona misma, el roster del servicio o un sistema';
    case 'self_leader_roster_service': return 'la persona misma, su líder directo, el roster del servicio o un sistema';
    case 'scoped': return 'cualquiera; las aristas persona ↔ cliente según scope (all / subtree / self)';
  }
}
